import React, { useState, useEffect, useMemo, useRef } from "react";

import "../App.css";
import "bootstrap/dist/css/bootstrap.min.css";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Card from "react-bootstrap/Card";
import Button from "react-bootstrap/Button";
import InputGroup from "react-bootstrap/InputGroup";
import Form from "react-bootstrap/Form";

import Markdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import { getEncoding, encodingForModel } from "js-tiktoken";

import { SSE } from "sse";
import { ResetAllButton } from "./ResetAllButton";

const TOKEN_LIMITS = {
  "gpt-4": 8000,
  "gpt-4-turbo-preview": 120000,
  "gpt-3.5-turbo": 4050,
};

function Gpt(props) {
  const [messages, setMessages] = React.useState([]);
  const [hasStarted, setHasStarted] = React.useState(false);
  const [isStreaming, setIsStreaming] = React.useState(false);
  const [promptNum, setPromptNum] = React.useState(0);
  const [lastPromptRun, setLastPromptRun] = React.useState(-1);
  const [showAfter, setShowAfter] = React.useState(-1);
  const [showScroll, setShowScroll] = useState(false);
  const [showScrollBottom, setShowScrollBottom] = useState(false);
  const [usedPrompts, setUsedPrompts] = React.useState(new Set());
  const [clearConfirm, setClearConfirm] = React.useState(false);

  const prompts = props.workshopConfig.getPrompts({ handlePromptButtonClicked, setActiveTab: props.setActiveTab });

  const checkScrollTop = () => {
    const scrollPosition = window.pageYOffset;
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const isScrollable = documentHeight > windowHeight + 100;
    const shouldShow = isScrollable && (scrollPosition > 200 || scrollPosition + windowHeight < documentHeight - 200);

    setShowScroll(shouldShow);
    setShowScrollBottom(shouldShow);
  };

  const scrollTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const scrollBottom = () => {
    window.scrollTo({ top: document.documentElement.scrollHeight, behavior: "smooth" });
  };

  useEffect(() => {
    window.addEventListener("scroll", checkScrollTop);
    // return () => window.removeEventListener("scroll", checkScrollTop);
  }, []);

  useEffect(() => {
    if (props.activeTab === "gpt") {
      if (messages?.length < 1) {
        handlePromptButtonClicked(null, 0);

        if (!hasStarted) {
          const savedMessages = localStorage.getItem(props.storageKey);

          if (savedMessages) {
            try {
              let parsed = JSON.parse(savedMessages);
              if (parsed) {
                console.log(parsed);
                setMessages(parsed);
                setHasStarted(true);

                const restoredUsed = new Set();
                let maxShowAfter = -1;
                parsed.forEach(msg => {
                  if (msg.showAfter !== undefined && msg.showAfter >= 0) {
                    restoredUsed.add(msg.showAfter);
                    if (msg.showAfter > maxShowAfter) {
                      maxShowAfter = msg.showAfter;
                    }
                  }
                });
                setUsedPrompts(restoredUsed);
                setLastPromptRun(maxShowAfter);

                const nextPrompt = maxShowAfter + 1;
                if (nextPrompt < prompts.length) {
                  handlePromptButtonClicked(null, nextPrompt);
                } else if (maxShowAfter >= 0) {
                  handlePromptButtonClicked(null, maxShowAfter);
                }
              }
            } catch {
              console.log("Failed to parse saved messages.");
            }
          }
        }
      } else {
        setTimeout(() => {
          window.scrollTo({
            top: document.body.scrollHeight,
            behavior: "instant",
          });
        }, 50);
      }
    }
  }, [props.activeTab]);

  const requestId = React.useRef(0);
  const questionTextRef = useRef(null);

  let currentStreamId = useRef(null);
  let ignoreStreamId = useRef(null);

  let spinnerDisp = "";

  let mainSummary = <></>;
  if (messages !== undefined && messages.length > 0) {
    mainSummary = <MessagesDisplay messages={messages} setVoiceOver={props.setVoiceOver} setElevatorPitch={props.setElevatorPitch} />;
    spinnerDisp = "none";
  }

  let chatBoxTopMargin = hasStarted ? "20px" : "";

  let respDisp = hasStarted ? "" : "none";
  const stopVis = isStreaming ? "visible" : "hidden";

  let gptName = "GPT4";
  if (props.gptModel.includes("gpt-3.5")) {
    gptName = "GPT3.5";
  }

  return (
    <Container className="mt-4 mb-0">
      <Row className="mb-0">
        <Col>
          <Card>
            <Card.Header
              as="h4"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              Chat with {gptName}
              {messages?.length > 1 && (
                <div style={{ display: 'flex', gap: '0.3rem' }}>
                  <Button size="sm" style={{ fontSize: '0.65rem', padding: '0.15rem 0.4rem' }} variant={clearConfirm ? "danger" : "outline-secondary"} onClick={handleClearClick}>
                    {clearConfirm ? "Confirm Clear" : "Clear All Text"}
                  </Button>
                </div>
              )}
            </Card.Header>

            <Card.Body>
              <div style={{
                backgroundColor: "white",
                border: "2px solid #f05f40",
                borderRadius: "8px",
                padding: "10px 15px 15px 15px",
                margin: "10px 0",
                boxShadow: "0 0 10px rgba(240, 95, 64, 0.3)"
              }}>
                <h3 style={{ color: "#999", fontWeight: "bold", margin: "0 0 5px 0", fontSize: "12px", fontStyle: "italic" }}>
                  Instructions:
                </h3>
                <h4 style={{ color: "#f05f40ff", fontWeight: "bold", margin: "0 0 5px 0" }}>
                  {props.workshopConfig.instructionTitle}
                </h4>
                <p style={{ color: "#f05f40ff", fontWeight: "bold", margin: "0" }}>
                  {props.workshopConfig.instructionSubtitle}
                </p>
              </div>

              <section
                className="response-section"
                style={{ display: respDisp }}
              >
                {mainSummary}
              </section>
              <section
                className="input-section"
                style={{ marginTop: chatBoxTopMargin }}
              >
                <Form
                  onSubmit={(e) => {
                    onSubmit(e);
                  }}
                >
                  <div style={{ margin: "10px", marginBottom: "30px" }}>
                    {generatePromptButtons()}
                    {generateHelpText()}
                  </div>
                  <hr style={{ marginTop: "40px" }} />
                  <div style={{ position: "relative" }}>
                    <div style={{ 
                      position: "absolute", 
                      top: "8px", 
                      left: "50px", 
                      color: "#999", 
                      fontWeight: "bold", 
                      fontSize: "12px",
                      fontStyle: "italic",
                      zIndex: 10,
                      pointerEvents: "none"
                    }}>
                      Prompt for {gptName}:
                    </div>
                    <div
                      onInput={checkIfEmpty}
                      className="form-control orange-highlight"
                      contentEditable={!isStreaming}
                      ref={questionTextRef}
                      data-placeholder={
                        "Type your message to " + gptName + "..."
                      }
                      onKeyDown={checkForCtrlEnter}
                      style={{ minHeight: "6.5em", position: "relative", width: "100%" }}
                    >
                    </div>
                    <Button
                      variant="primary"
                      type="submit"
                      className="btn-gpt-go"
                      disabled={isStreaming}
                    >
                      Go
                    </Button>
                    <Button
                      variant="danger"
                      className="btn-gpt-stop"
                      style={{
                        visibility: stopVis,
                      }}
                      onClick={handleStopButton}
                    >
                      Stop
                    </Button>
                  </div>
                </Form>

              </section>
            </Card.Body>
            <Card.Footer
              style={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                padding: "0.4rem 0.75rem",
                marginBottom: "0",
                borderBottom: "none",
              }}
            >
              <div style={{ display: 'flex', gap: '0.3rem' }}>
                {messages?.length > 1 && (
                  <Button size="sm" style={{ fontSize: '0.65rem', padding: '0.15rem 0.4rem' }} variant={clearConfirm ? "danger" : "outline-secondary"} onClick={handleClearClick}>
                    {clearConfirm ? "Confirm Clear" : "Clear All Text"}
                  </Button>
                )}
              </div>
            </Card.Footer>
          </Card>
          <Button
            onClick={scrollTop}
            style={{
              display: showScroll ? "flex" : "none",
              position: "fixed",
              bottom: "80px",
              right: "20px",
              zIndex: "1000",
            }}
            variant="secondary"
          >
            ↑
          </Button>
          <Button
            onClick={scrollBottom}
            style={{
              display: showScrollBottom ? "flex" : "none",
              position: "fixed",
              bottom: "20px",
              right: "20px",
              zIndex: "1000",
            }}
            variant="secondary"
          >
            ↓
          </Button>
        </Col>
      </Row>
    </Container>
  );

  function generateHelpText() {
    if (prompts[promptNum] && "helpText in prompts[promptNum]") {
      return <>{prompts[promptNum]["helpText"]}</>;
    }
    return <></>;
  }

  function generatePromptButtons() {
    const baseButtonStyle = { 
      marginRight: "10px", 
      marginBottom: "0px",
      whiteSpace: "nowrap",
      flexShrink: 0,
      transition: "all 0.3s ease"
    };
    
    const usedButtonStyle = {
      ...baseButtonStyle,
      backgroundColor: "#f0f8ff",
      borderColor: "#6c757d",
      color: "#6c757d"
    };
    
    let primaryButtons = [];
    let advancedButtons = [];
    
    for (let i = 0; i < prompts.length; i++) {
      const isUsed = usedPrompts.has(i);
      let variant = "outline-secondary";
      let btnClass = "";
      let buttonStyle = isUsed ? { ...usedButtonStyle } : { ...baseButtonStyle };
      
      if (promptNum == i) {
        variant = "outline-primary";
        buttonStyle = {
          ...baseButtonStyle,
          fontSize: "1.2em",
          fontWeight: "bold",
          border: "2px solid #007bff",
          boxShadow: "0 0 5px rgba(0, 123, 255, 0.3)"
        };
      }
      
      if (lastPromptRun + 1 == i && !isStreaming) {
        btnClass = "animated-outline";
      }
      
      const button = (
        <Button
          variant={variant}
          style={buttonStyle}
          key={i}
          onClick={(e) => handlePromptButtonClicked(e, i)}
          className={btnClass}
        >
          {i < 6 ? `${i + 1}. ` : ''}{prompts[i]["title"]}
        </Button>
      );
      
      if (i < 6) {
        primaryButtons.push(button);
      } else {
        advancedButtons.push(button);
      }
    }
    
    return (
      <div>
        {/* Always show buttons 1-6 */}
        <div style={{
          display: "flex",
          overflowX: "auto",
          paddingBottom: "10px",
          gap: "10px",
          scrollbarWidth: "thin",
          scrollbarColor: "#ccc #f1f1f1",
          alignItems: "center"
        }}>
          {primaryButtons}
        </div>
        
        {/* Only show buttons 7-17 after image generation starts */}
        {props.imageGenerationStarted && (
          <>
            <hr style={{
              border: "none",
              borderTop: "1px solid #d3d3d3",
              margin: "5px 0 10px 0"
            }} />
            <div style={{
              display: "flex",
              flexWrap: "wrap",
              paddingBottom: "10px",
              gap: "10px",
              alignItems: "center"
            }}>
              {advancedButtons}
            </div>
          </>
        )}
      </div>
    );
  }

  function handlePromptButtonClicked(e, index) {
    if (index < 0 || index >= prompts.length) {
      console.error("Invalid index");
      return;
    }

    setPromptNum(index);

    let htmlContent = "";
    let fillInPosition;

    const filmIdeas = props.workshopConfig.filmIdeas;
    const randomNumber = Math.floor(Math.random() * filmIdeas.length);

  let fillInStr = prompts[index]["fillIn"];
  if (index == 0) {
    fillInStr = filmIdeas[randomNumber];
  }
    // fillInStr='<span style="font-size:120%; color: #f05f40">' + fillInStr + '</span>';

    const values = prompts[index]["promptVal"];
    if (values.length === 2) {
      fillInPosition = values[0].length;
      htmlContent = `${values[0]}${fillInStr}${values[1]}`;
    } else {
      htmlContent = values[0];
    }

    questionTextRef.current.innerHTML = htmlContent;

    if (fillInPosition !== undefined) {
      highlightText(fillInPosition, fillInStr.length);
    }

    // questionTextRef.current.innerHTML = `${filmIdeaPrompt1}<strong>${e.target.value}</strong>${filmIdeaPrompt2}`
    // setFilmIdeaPrompt()
  }

  function highlightText(startPosition, length) {
    const range = document.createRange();
    const selection = window.getSelection();

    range.setStart(questionTextRef.current.firstChild, startPosition);
    range.setEnd(questionTextRef.current.firstChild, startPosition + length);

    selection.removeAllRanges();
    selection.addRange(range);
  }

  // function highlightText(startPosition, length) {
  //   const range = document.createRange();
  //   const selection = window.getSelection();
  //   const node = questionTextRef.current.childNodes[0]; // Assuming the first child is the text node, this might need adjustment

  //   try {
  //     range.setStart(node, startPosition);
  //     range.setEnd(node, startPosition + length);
  //     selection.removeAllRanges();
  //     selection.addRange(range);
  //   } catch (error) {
  //     console.error('Error highlighting text:', error);
  //   }
  // }

  function saveMesssagesToLocalStorage(newMessages) {
    try {
      localStorage.setItem(props.storageKey, JSON.stringify(newMessages));
    } catch (e) {
      console.error("An error occurred while saving to Local Storage", e);
    }
  }

  function checkIfEmpty() {
    if (questionTextRef.current.innerHTML === "<br>") {
      questionTextRef.current.innerHTML = "";
    }
  }

  function checkForCtrlEnter(e) {
    if (!e.ctrlKey && !e.shiftKey && e.code == "Enter") {
      onSubmit(e);
    }
  }

  function reset() {
    setHasStarted(false);
    requestId.current += 1;
    setMessages([]);
    setUsedPrompts(new Set());
    try {
      localStorage.setItem(props.storageKey, JSON.stringify([]));
    } catch (e) {
      console.error("Failed to save messages to localStorage:", e);
    }
    questionTextRef.current.focus();
  }

  function handleClearClick() {
    if (clearConfirm) {
      reset();
      setClearConfirm(false);
      return;
    }
    setClearConfirm(true);
    setTimeout(() => {
      setClearConfirm(false);
    }, 5000);
  }

  function MessagesDisplay(props) {
    let counter = 0;
    let out = [];
    let nextIsVoiceOver = false;
    let nextIsElevatorPitch = false;
    
    if (props.messages.length === 0) {
      return (
        <div style={{
          padding: "15px",
          backgroundColor: "#ebebeb",
          borderRadius: "5px"
        }}>
          <div style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "20px"
          }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <span style={{ marginLeft: "10px", color: "#666" }}>Generating response...</span>
          </div>
        </div>
      );
    }
    
    for (let message of props.messages) {
      out.push(<SingleMessageDisplay message={message} key={counter} couldBeVoiceOver={nextIsVoiceOver} setVoiceOver={props.setVoiceOver} setElevatorPitch={props.setElevatorPitch} couldBeElevatorPitch={nextIsElevatorPitch} />);

      if (message["role"] === "user" && message["content"].includes("voice-over monologue")) {
        nextIsVoiceOver = true;
      } else {
        nextIsVoiceOver = false;
      }

      if (message["role"] === "user" && message["content"].includes("elevator pitch for the film")) {
        nextIsElevatorPitch = true;
      } else {
        nextIsElevatorPitch = false;
      }
      counter += 1;
    }

    return (
      <div>
        <div>{out}</div>
      </div>
    );
  }

  function limitMessagesByTokenCount(messages, tokenLimit) {
    const enc = encodingForModel("gpt-4");

    let totalTokens = 0;
    let processed = [];

    for (let i = messages.length - 1; i >= 0; i--) {
      const tokens = enc.encode(messages[i]["content"]);
      if (totalTokens + tokens.length > tokenLimit) {
        break;
      }
      totalTokens += tokens.length;
      processed.unshift(messages[i]);
    }

    console.log(totalTokens);

    return processed;
  }

  function SingleMessageDisplay(props) {
    const bgColor =
      props.message["role"] === "assistant" ? "#ebebeb" : "#007bff";
    const fgColor =
      props.message["role"] === "assistant" ? "#000000" : "white";
    const textSize = props.message["role"] === "assistant" ? "normal" : "large";
    const fontWeight = props.message["role"] === "user" ? "bold" : "normal";
    const borderStyle = props.message["role"] === "user" ? "2px solid #007bff" : "none";

    const messageRef = useRef(null);

    const [buttonLabel, setButtonLabel] = useState("Copy");

    const copyToClipboard = async () => {
      if (messageRef && messageRef.current) {
        try {
          if (typeof ClipboardItem !== "undefined") {
            const html = new Blob([messageRef.current.innerHTML], {
              type: "text/html",
            });
            const text = new Blob([props.message["content"]], {
              type: "text/plain",
            });
            const data = new ClipboardItem({
              "text/html": html,
              "text/plain": text,
            });
            await navigator.clipboard.write([data]);
          } else {
            const cb = (e) => {
              e.clipboardData.setData(
                "text/html",
                messageRef.current.innerHTML
              );
              e.clipboardData.setData("text/plain", props.message["content"]);
              e.preventDefault();
            };
            document.addEventListener("copy", cb);
            document.execCommand("copy");
            document.removeEventListener("copy", cb);
          }

          // await navigator.clipboard.writeText(messageRef.current.innerHTML);
          setButtonLabel("Copied!");
          setTimeout(() => {
            setButtonLabel("Copy");
          }, 1000);
        } catch (err) {
          console.error("Failed to copy text: ", err);
        }
      }
    };

    let this_message = <></>;
    if (props.message["role"] === "assistant") {
      if (props.message["content"] === "") {
        this_message = (
          <div style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "20px"
          }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <span style={{ marginLeft: "10px", color: "#666" }}>Generating response...</span>
          </div>
        );
      } else {
        this_message = (
          <div ref={messageRef}>
            <Markdown remarkPlugins={[remarkBreaks]}>
              {props.message["content"]}
            </Markdown>
          </div>
        );
      }
    } else {
      this_message = <span ref={messageRef}>{props.message["content"]}</span>;
    }

    let showAfterResult = <></>;
    console.log(props.message["showAfter"]);
    if (props.message["showAfter"] !== undefined && props.message["showAfter"] >= 0) {
      showAfterResult = prompts[props.message["showAfter"]].showAfter;
      console.log(showAfterResult);
    }

    if (props.message["showAfter"] === 3 || props.couldBeVoiceOver) {
      props.setVoiceOver(props.message["content"]);
    }

    if (props.message["showAfter"] === 4 || props.couldBeElevatorPitch) {
      props.setElevatorPitch(props.message["content"]);
    }

    return (
      <div
        style={{
          padding: "15px",
          // whiteSpace: "pre-wrap",
          backgroundColor: bgColor,
          color: fgColor,
          fontSize: textSize,
          fontWeight: fontWeight,
          border: borderStyle,
          position: "relative",
        }}
      >
        {props.message["role"] === "user" && (
          <div style={{ 
            position: "absolute", 
            top: "5px", 
            left: "10px", 
            fontWeight: "bold", 
            color: "#ddd",
            fontSize: "12px",
            fontStyle: "italic",
            zIndex: 1
          }}>
            Prompt for GPT4:
          </div>
        )}
        {props.message["role"] === "assistant" && (
          <div style={{ 
            position: "absolute", 
            top: "5px", 
            left: "10px", 
            fontWeight: "bold", 
            color: "#999",
            fontSize: "12px",
            fontStyle: "italic",
            zIndex: 1
          }}>
            GPT4 Output:
          </div>
        )}
        <div style={{ 
          position: "relative", 
          paddingBottom: props.message["role"] === "user" ? "10px" : "35px",
          paddingTop: props.message["role"] === "user" ? "15px" : "20px"
        }}>
          {this_message}
          {props.message["role"] === "assistant" && (
            <Button
              variant="outline-secondary"
              size="sm"
              style={{
                position: "absolute",
                bottom: "-5px",
                right: "10px",
              }}
              onClick={copyToClipboard}
            >
              {buttonLabel}
            </Button>
          )}
        </div>
        {showAfterResult}
      </div>
    );
  }

  function getTextWithLineBreaks(divRef) {
    if (!divRef.current) return "";

    let htmlContent = divRef.current.innerHTML;

    htmlContent = htmlContent.replace(/<div>/g, "\n").replace(/<\/div>/g, "");
    htmlContent = htmlContent.replace(/<p>/g, "\n").replace(/<\/p>/g, "");

    htmlContent = htmlContent.replace(/<br\s*\/?>/g, "\n");

    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlContent;

    return tempDiv.textContent;
  }

  function handleStopButton(e) {
    ignoreStreamId.current = currentStreamId.current;
    currentStreamId.current = "STOP_NOW";
    setIsStreaming(false);
  }

  function removeShowAfter(objectsArray) {
    return objectsArray.map((obj) => {
      const { showAfter, ...rest } = obj;
      return rest;
    });
  }

  function onSubmit(e) {
    e.preventDefault();

    if (isStreaming) {
      return;
    }

    setHasStarted(true);
    setIsStreaming(true);
    const currentShowAfter = promptNum;
    setShowAfter(promptNum);
    setLastPromptRun(promptNum);
    
    setUsedPrompts(prev => new Set([...prev, promptNum]));
    
    setPromptNum(-1);

    currentStreamId.current = null;

    let text = getTextWithLineBreaks(questionTextRef);
    let new_message = { role: "user", content: text };
    questionTextRef.current.innerHTML = "";

    let token_limit = 4050;
    if (props.gptModel in TOKEN_LIMITS) {
      token_limit = TOKEN_LIMITS[props.gptModel];
    }
    console.log("token limit: " + token_limit);

    const message_limited = limitMessagesByTokenCount(
      [...removeShowAfter(messages), new_message],
      token_limit
    );

    console.log(message_limited);

    let fetch_data = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + props.openaiApiKey,
      },
      payload: JSON.stringify({
        model: props.gptModel,
        stream: true,
        max_tokens: 4096,
        messages: message_limited,
      }),
      // payload: JSON.stringify(truncateWrapper({
      //   model: 'gpt-4',
      //   messages: [...messages, new_message],
      // }))
    };

    askQuestion(fetch_data, new_message, 1, currentShowAfter);
  }

  function handleStreamError(e, attempt, fetch_data, new_message) {
    console.log("handleStreamError");

    let error_out = "";

    try {
      let error = JSON.parse(e.data);
      console.log(error);
      if ("error" in error && "message" in error["error"]) {
        error_out = error["error"]["message"];
      } else {
        error_out = error;
      }
    } catch {
      error_out = "failed to parse error data" + e;
    }

    console.log(`Stream error encountered. Attempt: ${attempt}`, error_out);

    const maxAttempts = 5;

    if (attempt < maxAttempts) {
      const jitter = Math.floor(Math.random() * 20);
      const delay = Math.pow(2, attempt) * 200 + jitter;

      console.log(`Retrying in ${delay}ms...`);

      setTimeout(() => {
        askQuestion(fetch_data, new_message, attempt + 1, showAfter);
      }, delay);
    } else {
      console.log("Maximum retry attempts reached. Aborting.");
      props.setErrorMessage(error_out);
      setIsStreaming(false);
    }
  }

  function handleStreamAbort(e) {
    console.log("stream abort");
    console.log(e);
  }

  function askQuestion(fetch_data, new_message, attempt, currentShowAfter) {
    var source = new SSE(
      "https://api.openai.com/v1/chat/completions",
      fetch_data
    );

    let newMessages = [
      ...messages,
      new_message,
      { role: "assistant", content: "", showAfter: currentShowAfter },
    ];
    console.log(newMessages);
    setMessages(newMessages);

    source.addEventListener("error", (e) =>
      handleStreamError(e, attempt, fetch_data, new_message)
    );
    source.addEventListener("abort", handleStreamAbort);

    source.addEventListener("message", function (e) {
      if (e.data != "[DONE]") {
        let result = JSON.parse(e.data);

        if (
          currentStreamId.current == null &&
          ignoreStreamId.current != result.id
        ) {
          currentStreamId.current = result.id;
        } else if (currentStreamId.current != result.id) {
          console.log("ignorning non-current id return!");
          return;
        }

        if (result?.choices?.[0]?.delta?.content) {
          const output = result.choices[0].delta.content;

          let newMessages2 = [...newMessages];
          newMessages2[newMessages2.length - 1]["content"] =
            newMessages2[newMessages2.length - 1]["content"] + output;
          setMessages(newMessages2);
        } else if (result && !result.choices) {
          props.setErrorMessage("Error: " + result);
          setIsStreaming(false);
        }
      } else {
        setIsStreaming(false);
        saveMesssagesToLocalStorage(newMessages);
      }
    });

    console.log("starting stream");
    source.stream();
  }
}
export { Gpt };
